import { segmentEventPush } from '../../../modules/ga/GTMdatalayer';

export const getRedirectionType = (): string => {
  if (typeof window === 'undefined') return '';
  const pathname = window.location.pathname.replace(/^\/|\/$/g, '');
  const responseType = new URLSearchParams(window.location.search).get('responseType');
  const validResponseType = responseType?.toLowerCase() === 'sass' || responseType?.toLowerCase() === 'vertex' ? responseType : null;
  return validResponseType ? `${pathname}|${validResponseType}` : pathname;
};

type FacetsQuery = Record<string, string[]>;

interface FacetValue {
  code: string;
  name: string;
}

interface FacetItem {
  code: string;
  name: string;
  values?: FacetValue[];
}

export interface FilterEntry {
  section: number;
  position: number;
  facet_key: string;
  facet_value: string;
  value: string;
  filterid: string;
}

export interface InlineFilterTab {
  inlineFilterId: string;
  name: string;
}

/**
 * Computes inline_filter_position: the last product position below which the filter is shown.
 * The grid inserts an InlineFilters row after every `frequency` product rows, with 2 products
 * per row (columnsCount = 2). So:
 *   inline_filter_position = (filterIndex + 1) × frequency × 2
 * e.g. filterIndex=0, frequency=6 → position 12 (after the 12th product)
 */
export const computeInlineFilterPosition = (filterIndex: number, frequency: number): number => {
  const COLUMNS = 2;
  return (filterIndex + 1) * frequency * COLUMNS;
};

/** Build filters array for segment events from a facetsQuery map */
export const buildFiltersArray = (
  facetsQuery: FacetsQuery,
  allfacetsItems: FacetItem[],
  section: number,
  positionOverride?: number,
  positionMap?: Record<string, number>
): FilterEntry[] => {
  const filters: FilterEntry[] = [];
  let position = 1;
  for (const [facetCode, selectedValues] of Object.entries(facetsQuery)) {
    const matchingFacet = allfacetsItems.find((f: FacetItem) => f.code === facetCode);
    for (const valueCode of selectedValues) {
      const facetValueObj = matchingFacet?.values?.find((v: FacetValue) => v.code === valueCode);
      const resolvedPosition =
        positionMap?.[`${facetCode}|${valueCode}`] ??
        (positionOverride != null ? positionOverride : position++);
      filters.push({
        section,
        position: resolvedPosition,
        facet_key: facetCode,
        facet_value: valueCode,
        value: facetValueObj?.name || valueCode,
        filterid: ''
      });
    }
  }
  return filters;
};

/** Return facets that were added (added=true) or removed (added=false) between prev and next */
export const getDiffFacets = (
  prev: FacetsQuery,
  next: FacetsQuery,
  added: boolean
): FacetsQuery => {
  const result: FacetsQuery = {};
  const source = added ? next : prev;
  const compare = added ? prev : next;
  for (const [key, values] of Object.entries(source)) {
    const compareValues = compare[key] || [];
    const diff = values.filter(v => !compareValues.includes(v));
    if (diff.length > 0) result[key] = diff;
  }
  return result;
};

/** Fire "Inline Filter Seen" segment event */
export const fireInlineFilterSeen = ({
  filterPosition,
  inlineFilters,
  activeTabName,
  tagsCount,
  session_id = '',
  screen_title = '',
  search_term = '',
  raw_query = '',
  redirection_type = ''
}: {
  filterPosition: number;
  inlineFilters: InlineFilterTab[];
  activeTabName: string;
  tagsCount: number;
  session_id?: string;
  screen_title?: string;
  search_term?: string;
  raw_query?: string;
  redirection_type?: string;
}): void => {
  const searchParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const search_type = searchParams.get('search_type') || 'usercompleted';
  const isCuratedWidget = searchParams.get('isCuratedWidget') === 'true';
  const isDynamicWidget = searchParams.get('isDynamicWidget') === 'true';
  const isBannerClick = searchParams.get('isBannerClick') === 'true';
  const screen_source = (() => {
    if (isCuratedWidget || isDynamicWidget) return 'widget';
    if (isBannerClick) return 'banner';
    return 'search';
  })();

  segmentEventPush({
    event: 'Inline Filter Seen',
    inline_filter_count: `${inlineFilters.length}|${tagsCount}`,
    preselected_filter: activeTabName,
    inline_filter_position: filterPosition,
    session_id,
    screen_title,
    screen_source,
    search_type,
    search_term,
    raw_query,
    redirection_type
  });
};

/** Fire "Inline Filter Clicked" segment event (tab click in section 0) */
export const fireInlineFilterClicked = ({
  filterPosition,
  inlineFilters,
  clickedTabId,
  clickedTabName,
  tagsCount,
  session_id = '',
  screen_title = '',
  search_term = '',
  raw_query = '',
  redirection_type = ''
}: {
  filterPosition: number;
  inlineFilters: InlineFilterTab[];
  clickedTabId: string;
  clickedTabName: string;
  tagsCount: number;
  session_id?: string;
  screen_title?: string;
  search_type?: string;
  search_term?: string;
  raw_query?: string;
  redirection_type?: string;
}): void => {
  const clickedPosition = inlineFilters.findIndex(f => f.inlineFilterId === clickedTabId) + 1;
  const defaultFilter = inlineFilters[0]?.name || '';
  const searchParams =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();
  const search_type = searchParams.get('search_type') || 'usercompleted';
  const isCuratedWidget = searchParams.get('isCuratedWidget') === 'true';
  const isDynamicWidget = searchParams.get('isDynamicWidget') === 'true';
  const isBannerClick = searchParams.get('isBannerClick') === 'true';
  const screen_source = (() => {
    if (isCuratedWidget || isDynamicWidget) return 'widget';
    if (isBannerClick) return 'banner';
    return 'search';
  })();

  segmentEventPush({
    event: 'Inline Filter Clicked',
    inline_filter_count: `${inlineFilters.length}|${tagsCount}`,
    inline_filter_selected: clickedTabName,
    inline_filter_position: filterPosition,
    default_filter_selected: defaultFilter,
    inline_filter_section_position: clickedPosition,
    session_id,
    screen_title,
    screen_source,
    search_type,
    search_term,
    raw_query,
    redirection_type
  });
};

/** Fire "Filter Applied" segment event */
export const fireFilterApplied = (
  appliedThrough: string,
  filters: FilterEntry[],
  extraParams: Record<string, string> = {}
): void => {
  if (!filters.length) return;

  const searchParams =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();
  const search_type = searchParams.get('search_type') || 'usercompleted';
  const isCuratedWidget = searchParams.get('isCuratedWidget') === 'true';
  const isDynamicWidget = searchParams.get('isDynamicWidget') === 'true';
  const isBannerClick = searchParams.get('isBannerClick') === 'true';
  const screen_source = (() => {
    if (isCuratedWidget || isDynamicWidget) return 'widget';
    if (isBannerClick) return 'banner';
    return 'search';
  })();

  const { screen_title = '', search_term = '', raw_query = '' } = extraParams;

  segmentEventPush({
    event: 'Filter Applied',
    applied_through: appliedThrough,
    filters,
    screen_title,
    screen_source,
    search_type,
    search_term,
    raw_query,
    redirection_type: getRedirectionType()
  });
};

/** Fire "Filter Removed" segment event */
export const fireFilterRemoved = (
  removedThrough: string,
  filtersDeselected: FilterEntry[],
  extraParams: Record<string, string> = {}
): void => {
  if (!filtersDeselected.length) return;

  const searchParams =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();
  const search_type = searchParams.get('search_type') || 'usercompleted';
  const isCuratedWidget = searchParams.get('isCuratedWidget') === 'true';
  const isDynamicWidget = searchParams.get('isDynamicWidget') === 'true';
  const isBannerClick = searchParams.get('isBannerClick') === 'true';
  const screen_source = (() => {
    if (isCuratedWidget || isDynamicWidget) return 'widget';
    if (isBannerClick) return 'banner';
    return 'search';
  })();

  const { screen_title = '', search_term = '', raw_query = '' } = extraParams;

  segmentEventPush({
    event: 'Filter Removed',
    removed_through: removedThrough,
    filters_deselected: filtersDeselected,
    screen_title,
    screen_source,
    search_type,
    search_term,
    raw_query,
    redirection_type: getRedirectionType()
  });
};
